import {
  VILNIUS_MODULAR_CITY_STANDALONE_COLLISION_VALUES,
} from "./modularCityFootprints.generated.ts";

export const VILNIUS_MODULAR_CITY_ASSET_MANIFEST_URL =
  "/assets/modular-city/manifest.json";

export interface ModularCityStandaloneCollisionSpec {
  readonly id: string;
  readonly tileId: `${number}:${number}`;
  /** Ground contact at the centre of the closed kiosk body. */
  readonly origin: Readonly<{ x: number; y: number; z: number }>;
  readonly rotationYRadians: number;
  /** Simplified enclosed body; canopy and service counter stay visual-only. */
  readonly bodySizeM: Readonly<{ x: number; y: number; z: number }>;
}

export const VILNIUS_MODULAR_CITY_STANDALONE_COLLISIONS:
  readonly Readonly<ModularCityStandaloneCollisionSpec>[] =
    VILNIUS_MODULAR_CITY_STANDALONE_COLLISION_VALUES.map((collision) =>
      Object.freeze({
        ...collision,
        tileId: collision.tileId as `${number}:${number}`,
      })
    );

const STANDALONE_COLLISIONS_BY_TILE = new Map<
  string,
  readonly Readonly<ModularCityStandaloneCollisionSpec>[]
>();
for (const collision of VILNIUS_MODULAR_CITY_STANDALONE_COLLISIONS) {
  const entries = STANDALONE_COLLISIONS_BY_TILE.get(collision.tileId) ?? [];
  STANDALONE_COLLISIONS_BY_TILE.set(
    collision.tileId,
    Object.freeze([...entries, collision]),
  );
}

export function modularCityStandaloneCollisionsForTile(
  tileId: string,
): readonly Readonly<ModularCityStandaloneCollisionSpec>[] {
  return STANDALONE_COLLISIONS_BY_TILE.get(tileId) ?? [];
}

/** Spatial bucket size balances prop-level culling with instanced draw calls. */
export const VILNIUS_MODULAR_CITY_BUCKET_SIZE_M = 768;

/** Full Blender detail is used only close enough for the third-person camera. */
export const VILNIUS_MODULAR_CITY_FULL_DETAIL_DISTANCE_M = 145;

/** Beyond the grounded/flight fog envelope, even LOD1 prop buckets stay hidden. */
export const VILNIUS_MODULAR_CITY_MAXIMUM_DISTANCE_M = 2_600;

/**
 * Keep small storefront and street-prop geometry alive just beyond the linear
 * fog horizon so meshes cannot pop while their origin is hidden but an edge is
 * still faintly visible.
 */
export const VILNIUS_MODULAR_CITY_VISIBILITY_MARGIN_M = 32;

/** Recompute spatial visibility only after meaningful player travel. */
export const VILNIUS_MODULAR_CITY_VISIBILITY_REFRESH_DISTANCE_M = 8;

/** Keep background decoding subordinate to gameplay and hero landmarks. */
export const VILNIUS_MODULAR_CITY_ASSET_LOAD_CONCURRENCY = 3;

/** Full archetypes decode only after the player has paused nearby. */
export const VILNIUS_MODULAR_CITY_FULL_DETAIL_STABLE_FOCUS_MS = 1_800;
export const VILNIUS_MODULAR_CITY_FULL_DETAIL_FOCUS_MOVEMENT_M = 4;

/** Leave breathing room between full archetype decode/compile tasks. */
export const VILNIUS_MODULAR_CITY_FULL_DETAIL_LOAD_GAP_MS = 250;
